import React, { useEffect, useMemo, useState } from "react";
import { useLang } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import api, { extractErrorMessage } from "@/lib/api";
import PublicLayout from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, Loader2, BellRing } from "lucide-react";

function fmtLocal(iso, tz) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: tz, dateStyle: "medium", timeStyle: "short",
    }).format(new Date(iso));
  } catch { return iso; }
}

const FILTERS = ["all", "confirmed", "cancelled"];

export default function MyBookings() {
  const { t } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [pendingCancel, setPendingCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [copied, setCopied] = useState(false);
  const browserTz = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "UTC"; }
  }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const { data } = await api.get(`/calendar/bookings`, { params });
      setItems(data.items || []);
    } catch (err) {
      toast.error(extractErrorMessage(err, t("apiFallback.serverError")));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const confirmCancel = async () => {
    if (!pendingCancel) return;
    setCancelling(true);
    try {
      await api.post(
        `/calendar/bookings/${pendingCancel.id}/cancel`,
        { cancelled_reason: cancelReason || null },
      );
      toast.success(t("calendarMVP.host.cancelDialog"));
      setPendingCancel(null);
      setCancelReason("");
      await reload();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("apiFallback.serverError")));
    } finally { setCancelling(false); }
  };

  if (!user) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-2xl px-4 py-16 text-center" data-testid="my-bookings-not-logged-in">
          <p className="text-slate-600">{t("calendarMVP.host.notLoggedIn")}</p>
        </div>
      </PublicLayout>
    );
  }

  // Mirror the backend derive_host_slug — sha256(owner_id)[:12]. We
  // don't compute it client-side; we just take it from the first
  // booking that has it. The host link is informational, so before any
  // booking exists, hide it gracefully.
  const sampleSlug = items.find((x) => x.host_slug)?.host_slug || null;

  return (
    <PublicLayout>
      <div className="mx-auto max-w-5xl px-4 py-8" data-testid="my-bookings-page">
        <div className="flex items-start gap-3 mb-6">
          <CalendarDays className="h-7 w-7 text-emerald-600 mt-1" />
          <div>
            <h1 className="text-2xl font-semibold text-slate-900" data-testid="my-bookings-title">
              {t("calendarMVP.host.title")}
            </h1>
            <p className="text-slate-600 text-sm mt-1">{t("calendarMVP.host.subtitle")}</p>
          </div>
        </div>

        {sampleSlug && (
          <Card className="p-4 mb-6 bg-emerald-50 border-emerald-200" data-testid="my-bookings-public-link-card">
            <div className="text-xs uppercase text-emerald-700 font-semibold tracking-wide">{t("calendarMVP.host.publicLink")}</div>
            <p className="text-sm text-slate-600 mt-1">{t("calendarMVP.host.publicLinkHint")}</p>
            {/* The host has at least one MT; we don't know the MT id w/o a list call,
                so we show a host-scoped URL placeholder for now. Each booking row
                also carries the MT id and slug for traceability. */}
            <div className="mt-2 flex flex-col sm:flex-row gap-2">
              <code className="flex-1 text-xs px-3 py-2 bg-white border border-emerald-200 rounded font-mono break-all">
                {`${window.location.origin}/book/${sampleSlug}/<meeting_type_id>`}
              </code>
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/book/${sampleSlug}/`);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                data-testid="my-bookings-copy-link"
              >
                {copied ? t("calendarMVP.host.publicLinkCopied") : t("calendarMVP.host.publicLinkCopy")}
              </Button>
            </div>
          </Card>
        )}

        <div className="flex gap-2 mb-4" data-testid="my-bookings-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-sm border transition ${
                filter === f
                  ? "bg-emerald-600 text-white border-emerald-600"
                  : "border-slate-200 text-slate-600 hover:border-slate-300"
              }`}
              data-testid={`my-bookings-filter-${f}`}
            >
              {t(`calendarMVP.host.filter${f.charAt(0).toUpperCase() + f.slice(1)}`)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12" data-testid="my-bookings-loading">
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-emerald-600" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-slate-500 italic" data-testid="my-bookings-empty">
            {filter === "all" ? t("calendarMVP.host.emptyAll") : t("calendarMVP.host.emptyFiltered")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border border-slate-200 rounded-lg overflow-hidden" data-testid="my-bookings-table">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">{t("calendarMVP.host.colWhen")}</th>
                  <th className="px-4 py-3">{t("calendarMVP.host.colMeeting")}</th>
                  <th className="px-4 py-3">{t("calendarMVP.host.colInvitee")}</th>
                  <th className="px-4 py-3">{t("calendarMVP.host.colStatus")}</th>
                  <th className="px-4 py-3 text-right">{t("calendarMVP.host.colActions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((b) => (
                  <tr key={b.id} className="border-t border-slate-100" data-testid={`my-bookings-row-${b.id}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium text-slate-900">{fmtLocal(b.slot_start_utc, browserTz)}</div>
                      <div className="text-xs text-slate-500">{b.host_timezone}: {fmtLocal(b.slot_start_utc, b.host_timezone)}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="text-slate-900">{b.meeting_type_name_snapshot}</div>
                      <div className="text-xs text-slate-500">{b.meeting_type_duration_min_snapshot} min</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="text-slate-900">{b.invitee_name}</div>
                      <div className="text-xs text-slate-500">{b.invitee_email}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <StatusBadge status={b.status} t={t} />
                      {(b.reminder_24h_sent_at || b.reminder_1h_sent_at) && (
                        <div className="flex items-center gap-1 mt-1 text-[11px] text-emerald-700">
                          <BellRing className="h-3 w-3" />
                          {t("calendarMVP.host.reminderSent")}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right align-top">
                      {b.status === "confirmed" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setPendingCancel(b); setCancelReason(""); }}
                          data-testid={`my-bookings-cancel-${b.id}`}
                        >
                          {t("calendarMVP.host.cancelCta")}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={!!pendingCancel} onOpenChange={(o) => !o && setPendingCancel(null)}>
          <DialogContent data-testid="my-bookings-cancel-dialog">
            <DialogHeader>
              <DialogTitle>{t("calendarMVP.host.cancelDialog")}</DialogTitle>
              <DialogDescription>{t("calendarMVP.host.cancelDialogBody")}</DialogDescription>
            </DialogHeader>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder={t("calendarMVP.host.cancelReason")}
              maxLength={200}
              data-testid="my-bookings-cancel-reason"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setPendingCancel(null)} data-testid="my-bookings-cancel-keep">
                {t("calendarMVP.host.cancelKeep")}
              </Button>
              <Button variant="destructive" onClick={confirmCancel} disabled={cancelling} data-testid="my-bookings-cancel-confirm">
                {cancelling ? t("calendarMVP.view.cancelling") : t("calendarMVP.host.cancelConfirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PublicLayout>
  );
}

function StatusBadge({ status, t }) {
  const cls = status === "confirmed"
    ? "bg-emerald-100 text-emerald-800"
    : "bg-slate-200 text-slate-700";
  const label = status === "confirmed"
    ? t("calendarMVP.view.statusConfirmed")
    : t("calendarMVP.view.statusCancelled");
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>
  );
}
